/* Platform chips (snippets/st-platform-chips.liquid), shared by the collection page
   (assets/st-collection.js) and search (assets/st-search.js), and the filter logic both use.
   - <st-platform-filter> ships hidden; this shows it. Its chips are toggle buttons, one pressed at a
     time. The pressed one is in the address as ?platform=<handle> (or ?platform=all when the page
     started on a platform and the shopper chose All), written with replaceState so Back still leaves
     the page, and read on load so a shared or reloaded link opens on the same platform. A handle no
     chip carries is ignored.
   - It does not show or hide tiles itself: it fires "change" and the page filters its own tiles by
     their data-st-platform with StFilter.match, together with any other filter it has (the
     collection's formats), and passes the new counts back with setCounts.
   StFilter is plain logic with no DOM, so scripts/qa/st-filter.test.mjs runs it in node. */
(function (root) {
  const ALL = 'all';
  const PARAM = 'platform';

  const StFilter = {
    PARAM,
    /** Whether an item ({ type, platform }) passes every filter in state; '' lets anything through. */
    match(item, state) {
      return Object.keys(state).every((key) => !state[key] || item[key] === state[key]);
    },
    /** How many items would show with state[key] set to value and the other filters as they are. */
    count(items, state, key, value) {
      const next = { ...state, [key]: value };
      return items.filter((item) => StFilter.match(item, next)).length;
    },
    /** The platform a query string asks for: a handle from values, '' for All, null for none of them. */
    fromSearch(search, values) {
      const raw = new URLSearchParams(search || '').get(PARAM);
      if (raw === null) return null;
      const value = raw.trim().toLowerCase();
      if (value === ALL) return '';
      return values.includes(value) ? value : null;
    },
    /** The address with the platform written in: none when it is the page's starting choice, else the handle or "all". */
    toHref(href, value, start) {
      const url = new URL(href);
      if (value === start) url.searchParams.delete(PARAM);
      else url.searchParams.set(PARAM, value || ALL);
      return `${url.pathname}${url.search}${url.hash}`;
    },
  };
  root.StFilter = StFilter;

  if (typeof customElements === 'undefined' || customElements.get('st-platform-filter')) return;

  customElements.define(
    'st-platform-filter',
    class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.stReady) return;
        this.dataset.stReady = 'true';
        this.chips = [...this.querySelectorAll('[data-st-plat]')];
        // The chip pressed in the markup: All, or on search the platform the query names.
        this.start = this.querySelector('[data-st-plat][aria-pressed="true"]')?.dataset.stPlat || '';
        const asked = StFilter.fromSearch(
          window.location.search,
          this.chips.map((chip) => chip.dataset.stPlat).filter(Boolean)
        );
        this.press(asked === null ? this.start : asked);
        this.hidden = false;
        this.addEventListener('click', (event) => {
          const chip = event.target.closest('[data-st-plat]');
          if (!chip || chip.dataset.stPlat === this.value) return;
          this.select(chip.dataset.stPlat);
        });
      }

      get value() {
        return this.current || '';
      }

      press(value) {
        this.current = value;
        this.chips.forEach((chip) => chip.setAttribute('aria-pressed', String(chip.dataset.stPlat === value)));
      }

      /** Choose a platform as the shopper did: pressed, written to the address, announced to the page. */
      select(value) {
        this.press(value);
        try {
          window.history.replaceState(window.history.state, '', StFilter.toHref(window.location.href, value, this.start));
        } catch (e) {
          // A sandboxed preview can refuse replaceState; the filter still works.
        }
        this.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /** Each chip's count, from { handle: n } ('' is All). */
      setCounts(counts) {
        this.chips.forEach((chip) => {
          const n = chip.querySelector('[data-st-n]');
          const value = counts[chip.dataset.stPlat];
          if (n && value !== undefined && n.textContent !== String(value)) n.textContent = String(value);
        });
      }
    }
  );
})(typeof window !== 'undefined' ? window : globalThis);
